import { Component } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgIf } from '@angular/common';
import { HeaderComponent } from '../../components/header/header.component';
import { TextInputComponent } from '../../components/text-input/text-input.component';
import { SelectInputComponent } from '../../components/select-input/select-input.component';
import { ButtonComponent } from '../../components/button/button.component';
import { FileUploaderComponent } from '../../components/file-uploader/file-uploader.component';
import { DropdownService } from '../../services/dropdown.service';
import { UserService } from '../../services/user.service';
import { GlobalService } from '../../services/global.service';
import { ValidationService } from '../../services/validation.service';
import { FilterByKeyAndValuePipe } from '../../pipes/filter-by-key-and-value.pipe';

@Component({
    selector: 'app-faculty-form-page',
    standalone: true,
    imports: [
        HeaderComponent,
        TextInputComponent,
        SelectInputComponent,
        ButtonComponent,
        FileUploaderComponent,
        ReactiveFormsModule,
        FormsModule,
        NgIf,
        FilterByKeyAndValuePipe
    ],
    templateUrl: './faculty-form-page.component.html'
})
export class FacultyFormPageComponent {
    form: FormGroup;
    selected_ids: any = {};
    selectedRole: any = null;
    showPasswordField = true;
    isSubmitting = false;
    isViewMode = false;

    constructor(
        public gs: GlobalService,
        public fb: FormBuilder,
        public us: UserService,
        public ar: ActivatedRoute,
        public ds: DropdownService,
        public vs: ValidationService,
        public route: Router
    ) {
        this.form = this.fb.group({
            role_id: ['', [Validators.required]],
            university_id: ['', [Validators.required]],
            college_id: ['', [Validators.required]],
            subject_ids: ['', [Validators.required]],
            name: ['', [Validators.required, Validators.minLength(this.vs.min_length_name), Validators.maxLength(this.vs.max_length_name), Validators.pattern(this.vs.new_name_pattern)]],
            email: ['', [Validators.required, Validators.pattern(this.vs.email_pattern), Validators.maxLength(this.vs.max_length_email)]],
            contact: ['', [Validators.required, Validators.minLength(this.vs.min_length_contact), Validators.maxLength(this.vs.max_length_contact)]],
            password: ['', [Validators.required, Validators.pattern(this.vs.password_complex_pattern)]],
            image: [''],
            is_active: [true],
            user_type: 'staff'
        });
    }

    async ngOnInit() {
        await this.ds?.get_dropdown_list(['role', 'university', 'exam', 'subject', 'college', 'academic-year']);

        this.form.get('role_id')?.valueChanges.subscribe(roleId => {
            this.selectedRole = this.ds.dropdown_list?.role_list?.find((r: any) => r.id === roleId);
            this.updateDynamicValidators();
        });

        this.form.get('university_id')?.valueChanges.subscribe(() => {
            this.form.get('subject_ids')?.reset();
        });

        this.ar.queryParams.subscribe(async params => {
            const id = params['id'];
            this.isViewMode = params['view'] === 'true';

            if (id) {
                await this.loadUser(id);
            } else {
                this.selected_ids = {};
                this.form.reset();
                this.showPasswordField = true;
            }

            if (this.isViewMode) {
                this.form.disable({ emitEvent: false });
            } else {
                this.form.enable({ emitEvent: false });
            }
        });
    }

    get pageTitle(): string {
        if (this.isViewMode) return 'View Faculty';
        return this.selected_ids?.id ? 'Update Faculty' : 'Add Faculty';
    }

    async loadUser(id: string) {
        const response = await this.us.get(id);
        this.selected_ids = { ...response.data };

        // Set selectedRole before patching so validators are correct
        this.selectedRole = this.ds.dropdown_list?.role_list?.find((r: any) => r.id === this.selected_ids.role_id);

        // Patch all fields except subject_ids first — university_id must be set so subject options get filtered
        const { subject_ids, ...rest } = this.selected_ids;
        this.form.patchValue(rest, { emitEvent: false });
        this.updateDynamicValidators();

        // After Angular re-evaluates the filtered subject options, patch subject_ids
        setTimeout(() => {
            this.form.patchValue({ subject_ids }, { emitEvent: false });
        });

        this.showPasswordField = false;
        this.form.get('password')?.clearValidators();
        this.form.get('password')?.reset();
        this.form.get('password')?.updateValueAndValidity();
    }

    async submit() {
        try {
            if (this.isSubmitting) return;
            if (this.form.invalid) {
                this.gs.toastr_shows_function('Please fill all required fields correctly.', 'Error', 'error');
                this.form.markAllAsTouched();
                return;
            }
            this.isSubmitting = true;
            let payload: any = { ...this.form.value, user_type: 'staff' };
            payload.user_type = 'staff';
            delete payload.roll_number;
            delete payload.birth_date;
            delete payload.registration_number;
            delete payload.academic_year_id;
            payload.is_active = true;

            if (this.selected_ids && this.selected_ids.id) {
                if (!this.showPasswordField) {
                    delete payload.password;
                }
                const update_response = await this.us.update(this.selected_ids.id, payload);
                this.gs.toastr_shows_function(update_response.message, 'Success', 'success');
            } else {
                const create_response = await this.us.add(payload);
                this.gs.toastr_shows_function(create_response?.message, 'Success', 'success');
            }

            this.route.navigate(['/user/list']);
        } catch (error: any) {
            this.gs.toastr_shows_function(error?.error?.message, 'Error', 'error');
        } finally {
            this.isSubmitting = false;
        }
    }

    cancel() {
        this.route.navigate(['/user/list']);
    }

    resetPassword() {
        this.showPasswordField = true;
        this.form.get('password')?.reset();
        this.form.get('password')?.setValidators([
            Validators.required,
            Validators.pattern(this.vs.password_complex_pattern)
        ]);
        this.form.get('password')?.updateValueAndValidity();
    }

    hidePassword() {
        this.showPasswordField = false;
        this.form.get('password')?.clearValidators();
        this.form.get('password')?.reset();
        this.form.get('password')?.updateValueAndValidity();
    }

    updateDynamicValidators() {
        const universityControl = this.form.get('university_id');
        const collegeControl = this.form.get('college_id');
        const subjectControl = this.form.get('subject_ids');

        universityControl?.clearValidators();
        collegeControl?.clearValidators();
        subjectControl?.clearValidators();

        if (!this.selectedRole || this.selectedRole.belongs_to === 'all') {
            // no validation
        } else if (this.selectedRole.belongs_to === 'university') {
            universityControl?.setValidators([Validators.required]);
        } else if (this.selectedRole.belongs_to === 'college') {
            universityControl?.setValidators([Validators.required]);
            collegeControl?.setValidators([Validators.required]);
        } else if (this.selectedRole.belongs_to === 'subject') {
            universityControl?.setValidators([Validators.required]);
            subjectControl?.setValidators([Validators.required]);
        }

        universityControl?.updateValueAndValidity();
        collegeControl?.updateValueAndValidity();
        subjectControl?.updateValueAndValidity();
    }
}
